import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../layouts/App/hooks/useAppSelector';
import { logout } from '../store/slices/authSlice';
import { areTokensValid } from '../services/auth/utils/tokenCache';
import FeedPage from './pages/FeedPage';
import TripsPage from './pages/TripsPage';
import TripDetailPage from './pages/TripDetailPage';
import CalendarPage from './pages/CalendarPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import ProfileTripsPage from './pages/ProfileTripsPage';
import PlaceholderPage from './pages/PlaceholderPage';
import AskAiPage from './pages/AskAiPage';
import ExplorePage from './pages/ExplorePage';
import ScrollToTop from './components/ScrollToTop';
import './styles/tokens.css';
import './V2App.css';

const V2App: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userProfile, restUser, isLoggedIn } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const isTestMode =
      process.env.NODE_ENV === 'development' && restUser?.email === 'test@example.com';

    if (isLoggedIn && !isTestMode && !areTokensValid()) {
      console.warn('⚠️ User is logged in but tokens are invalid, logging out for security');
      dispatch(logout());
      navigate('/', { replace: true });
    }
  }, [isLoggedIn, restUser, dispatch, navigate]);

  return (
    <div className="v2-app">
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Navigate to="/feed" replace />} />
        <Route
          path="feed"
          element={<FeedPage user={userProfile} restUser={restUser} />}
        />
        <Route
          path="trips"
          element={<TripsPage />}
        />
        <Route
          path="trips/:tripId"
          element={<TripDetailPage />}
        />
        <Route
          path="ask-ai"
          element={<AskAiPage />}
        />
        <Route
          path="explore"
          element={<ExplorePage />}
        />
        <Route
          path="messages"
          element={<PlaceholderPage section="messages" title="Messages" />}
        />
        <Route
          path="profile"
          element={<ProfilePage user={userProfile} restUser={restUser} />}
        />
        <Route
          path="profile/trips"
          element={<ProfileTripsPage user={userProfile} restUser={restUser} />}
        />
        <Route
          path="calendar"
          element={<CalendarPage />}
        />
        <Route
          path="settings"
          element={<SettingsPage user={userProfile} restUser={restUser} />}
        />
        <Route path="plan" element={<Navigate to="/explore" replace />} />
        <Route path="*" element={<Navigate to="/feed" replace />} />
      </Routes>
      <div id="v2-portal-root" />
    </div>
  );
};

export default V2App;
